import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { 
  Card, 
  CardPanel, 
  IconHighlight, 
  Typography, 
  Button,
  Icon,
  Skeleton,
  Tag,
  Select
} from "@ann/web-components";
import { getGarageVehicles } from '../../services/garage';

/**
 * Vehicle component for contact preview - shows vehicle info for a specific contact
 */
const VehiclesChatTool = observer(({ contactId }) => {
  const [vehicles, setVehicles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  // Load vehicles for this specific contact
  const loadVehicles = async () => {
    if (!contactId) return;
    
    try {
      setIsLoading(true);
      const response = await getGarageVehicles({
        party_uuid: contactId,
        page: 1,
        page_size: 10,
        order_by: ['created_at'],
        order_direction: ['DESC'],
        filters: {}
      });
      
      console.log('🚗 Contact vehicles loaded:', response);
      const vehiclesList = Array.isArray(response.body) ? response.body : [];
      setVehicles(vehiclesList);
      
      // Auto-select first vehicle for display
      if (vehiclesList.length > 0) {
        setSelectedVehicle(vehiclesList[0]);
      }
    } catch (err) {
      console.error('Error loading contact vehicles:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadVehicles();
  }, [contactId]);

  if (isLoading) {
    return (
      <Card size='large'>
        <CardPanel isExpandable={false} openDefault={true}>
          <CardPanel.Header title='Vehicle'>
            <CardPanel.Header.LeftSlot>
              <div className="header-left-slot">
                <Icon name='dragIndicator' size='medium' />
                <IconHighlight icon='directionsCar' bgColor='primary' iconColor='primary' size='medium' />
              </div>
            </CardPanel.Header.LeftSlot>
          </CardPanel.Header>
          <CardPanel.Content>
            <Skeleton height="250px" width="100%" />
          </CardPanel.Content>
        </CardPanel>
      </Card>
    );
  }

  if (vehicles.length === 0) {
    return (
      <Card size='large'>
        <CardPanel isExpandable={false} openDefault={true}>
          <CardPanel.Header title='Vehicle'>
            <CardPanel.Header.LeftSlot>
              <div className="header-left-slot">
                <Icon name='dragIndicator' size='medium' />
                <IconHighlight icon='directionsCar' bgColor='primary' iconColor='primary' size='medium' />
              </div>
            </CardPanel.Header.LeftSlot>
          </CardPanel.Header>
          <CardPanel.Content>
            <Typography category='body' weight='regular' size='small'>
              No vehicles found for this contact
            </Typography>
          </CardPanel.Content>
        </CardPanel>
      </Card>
    );
  }

  const vehicleInfo = selectedVehicle?.vehicleInformation || selectedVehicle;
  const vehicleName = `${vehicleInfo?.modelYear || ''} ${vehicleInfo?.make || ''} ${vehicleInfo?.model || ''}`.trim();

  return (
    <Card size='large'>
      <CardPanel isExpandable={false} openDefault={true}>
        <CardPanel.Header title='Vehicle'>
          <CardPanel.Header.LeftSlot>
            <div className="header-left-slot">
              <Icon name='dragIndicator' size='medium' />
              <IconHighlight icon='directionsCar' bgColor='primary' iconColor='primary' size='medium' />
            </div>
          </CardPanel.Header.LeftSlot>
        </CardPanel.Header>
        
        <CardPanel.Content>
          <div className="vehicle-widget-container">
            {/* Vehicle Selector */}
            <div className="vehicle-selector">
              <Select
                label="Vehicle"
                value={vehicleName || 'Unknown Vehicle'}
                options={vehicles.map(v => {
                  const info = v.vehicleInformation || v;
                  const name = `${info.modelYear || ''} ${info.make || ''} ${info.model || ''}`.trim();
                  return { label: name, value: v.vehicleInformation?.vehicleUuid || v.id };
                })}
                onChange={(selected) => {
                  const vehicle = vehicles.find(v => 
                    (v.vehicleInformation?.vehicleUuid || v.id) === selected.value
                  );
                  if (vehicle) setSelectedVehicle(vehicle);
                }}
              />
            </div>
            
            <div className="vehicle-content-preview">
              {/* Left side - Image and summary */}
              <div className="vehicle-left-preview">
                <div className="vehicle-image-container">
                  <img 
                    src="/ModelCar.png" 
                    alt="Vehicle" 
                    className="vehicle-main-image"
                  />
                </div>
                
                <div className="vehicle-summary">
                  <div className="summary-item">
                    <Typography category='body' weight='regular' size='medium'>
                      $10,000
                    </Typography>
                    <Tag text="Score 95" color='informative' size='medium' />
                  </div>
                  <div className="summary-item">
                    <Typography category='subtitle' weight='bold' size='small'>
                      Alerts
                    </Typography>
                    <Tag text="Service Overdue" color='alert' size='medium' />
                  </div>
                  <div className="vin-info">
                    <Typography category='subtitle' weight='bold' size='small'>VIN</Typography>
                    <div className="vin-display">
                      <Typography category='body' weight='regular' size='medium'>
                        {vehicleInfo?.vin || 'N/A'}
                      </Typography>
                      <Icon name="copy" size="small" style={{ cursor: 'pointer' }} />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Right side - Details */}
              <div className="vehicle-right-preview">
                <div className="detail-row">
                  <Typography category='subtitle' weight='bold' size='small'>Last Mileage</Typography>
                  <Typography category='body' weight='regular' size='medium'>
                    46,588 mi
                  </Typography>
                </div>
                
                <div className="detail-row">
                  <Typography category='subtitle' weight='bold' size='small'>Estimated Mileage</Typography>
                  <Typography category='body' weight='regular' size='medium'>
                    46,588 mi
                  </Typography>
                </div>
                
                <div className="detail-row">
                  <Typography category='subtitle' weight='bold' size='small'>Last Service Date</Typography>
                  <Typography category='body' weight='regular' size='medium'>
                    May 22, 2024
                  </Typography>
                </div>
                
                <div className="detail-row">
                  <Typography category='subtitle' weight='bold' size='small'>Purchased</Typography>
                  <Typography category='body' weight='regular' size='medium'>
                    New
                  </Typography>
                </div>
                
                <div className="detail-row">
                  <Typography category='subtitle' weight='bold' size='small'>Purchased Fate</Typography>
                  <Typography category='body' weight='regular' size='medium'>
                    Jun 1, 2023
                  </Typography>
                </div>
                
                <div className="detail-row">
                  <Typography category='subtitle' weight='bold' size='small'>Purchased Date</Typography>
                  <Typography category='body' weight='regular' size='medium'>
                    Jun 1, 2023
                  </Typography>
                </div>
                
                <div className="detail-row">
                  <Typography category='subtitle' weight='bold' size='small'>Key Features</Typography>
                  <Typography category='body' weight='regular' size='small'>
                    Heated seats - Adaptive cruise - AWD
                  </Typography>
                </div>
                
                <div className="detail-row">
                  <Typography category='subtitle' weight='bold' size='small'>Created Date</Typography>
                  <Typography category='body' weight='regular' size='medium'>
                    Sep 25, 2025
                  </Typography>
                </div>
                
                <div className="detail-row">
                  <Typography category='subtitle' weight='bold' size='small'>Last Updated</Typography>
                  <Typography category='body' weight='regular' size='medium'>
                    Nov 18, 2025, 06:36 PM
                  </Typography>
                </div>
              </div>
            </div>
          </div>
        </CardPanel.Content>
      </CardPanel>
    </Card>
  );
});

export default VehiclesChatTool;